import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Clock, Database, Lock, Shield, Trash2 } from 'lucide-react';

const ReEngageModal = ({ open, onConfirm, onLogout, auth }) => {

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
      TransitionProps={{ timeout: 500 }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6">
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please review the information below before continuing
          </Typography>
        </Box>

        <Box
          sx={{
            maxHeight: '70vh',
            overflowY: 'auto',
            px: 3,
            py: 3,
          }}
        >
          <Box className="space-y-6">
            <Card className="w-full bg-white dark:bg-[#1A1F2C] shadow-md">
              <CardHeader className="pb-24 pt-24 ph-24">
                <div className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  <CardTitle>Data Handling Overview</CardTitle>
                </div>
                <CardDescription>
                  Understanding what happens to your data
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-5 pt-0 pb-24 ph-24">
                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-gray-500" />
                    Data That Is Deleted
                  </h4>
                  <ul className="ml-24 mt-10 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• Company profile and configuration data</li>
                    <li>• Employee and workforce records</li>
                    <li>• Schedules, tasks, messages, and uploads</li>
                    <li>• Operational platform data</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Lock className="h-4 w-4 text-gray-500" />
                    Data That May Be Retained
                  </h4>
                  <ul className="ml-24 mt-10 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• Billing and invoicing records</li>
                    <li>• Payment transaction references</li>
                    <li>• System and delivery logs</li>
                    <li>• Compliance and audit records</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gray-500" />
                    Why Some Data Is Retained
                  </h4>
                  <ul className="ml-24 mt-10 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• Legal and accounting obligations</li>
                    <li>• Fraud prevention and disputes</li>
                    <li>• Third-party compliance</li>
                    <li>• System integrity</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="w-full bg-white dark:bg-[#1A1F2C] shadow-md">
              <CardHeader className="pb-24 pt-24 ph-24">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <CardTitle>Company Deletion Lifecycle</CardTitle>
                </div>
                <CardDescription>
                  Step-by-step deletion process
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 pt-0 pb-24 ph-24">
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <li>
                    <strong>1. Deletion Requested:</strong> Company enters pending
                    deletion state.
                  </li>
                  <li>
                    <strong>2. Cooldown Period:</strong> 14-day review window. Full
                    access remains. Deletion can be canceled.
                  </li>
                  <li>
                    <strong>3. Permanent Deletion:</strong> Eligible data is removed.
                    Some records may be retained.
                  </li>
                </ul>

                <Box className="bg-muted/50 rounded-lg p-16">
                  <Typography variant="body2" fontWeight={500}>
                    Why this process exists:
                  </Typography>
                  <ul className="mt-10 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• Prevents accidental deletion</li>
                    <li>• Protects against unauthorized actions</li>
                    <li>• Ensures legal and compliance safety</li>
                  </ul>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        {auth?.role === "companyUser" && (
          <Button
            onClick={onConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#078037',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#05662a',
              },
              '&:disabled': {
                backgroundColor: '#94d3a2',
              },
            }}
          >
            Re-Engage
          </Button>
        )}

        <Button
          onClick={onLogout}
          variant="outlined"
          sx={{
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReEngageModal;